import { Alert } from "antd";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { getUserDisplayName } from "../../../common/utils/userDisplayName";
import type { RootState } from "../../store";

type Props = {
  ticketId: string;
  status: string;
  standbyTo?: string | null;
  standbyFrom?: string | null;
};

export const DelayBanner = ({
  ticketId: _ticketId,
  status,
  standbyTo,
  standbyFrom,
}: Props) => {
  const latest = useSelector(
    (state: RootState) => state.ticket.ticketDelays?.[0] ?? null
  );

  // A future standby_from while the WO is not yet on standby means the standby was
  // scheduled: the WO keeps its current status until the scheduler flips it.
  const scheduledFor =
    status !== "STANDBY" && standbyFrom && dayjs(standbyFrom).isAfter(dayjs())
      ? standbyFrom
      : null;

  if (status !== "STANDBY" && !scheduledFor) return null;

  const reportedText = latest?.message
    ? `${latest.message} — reported by ${latest.sender ? getUserDisplayName(latest.sender) : "unknown"} on ${dayjs(latest.created_at).format("DD/MM/YYYY HH:mm")}`
    : "No standby reason provided";

  if (scheduledFor) {
    return (
      <Alert
        type="info"
        showIcon
        message="Standby Scheduled"
        description={`Standby scheduled for ${dayjs(scheduledFor).format("DD/MM/YYYY HH:mm")} — the work order stays active until then.`}
      />
    );
  }

  // An active standby carries its start only once it has taken effect (past),
  // so a backdated or scheduled-then-activated start is shown as "since". The
  // "until" phrasing drops the redundant "On standby" prefix once "since" leads.
  const sinceText = standbyFrom
    ? ` · On standby since ${dayjs(standbyFrom).format("DD/MM/YYYY HH:mm")}`
    : "";
  const untilText = standbyTo
    ? ` · ${standbyFrom ? "until" : "On standby until"} ${dayjs(standbyTo).format("DD/MM/YYYY HH:mm")}`
    : "";

  return (
    <Alert
      type="warning"
      showIcon
      message="Work Order On Standby"
      description={`${reportedText}${sinceText}${untilText}`}
    />
  );
};
